import React, { useMemo, useRef } from 'react';
import { useAsync } from 'react-use';
import * as R from 'ramda';

import { PosPlugin } from 'plugins/plugin';

const companyOrFullName = R.ifElse(
  R.propEq('customerType', 'COMPANY'),
  R.propOr('companyName', ''),
  R.compose(R.join(' '), R.props(['firstName', 'lastName'])),
);

function isMatchingSearchValue(searchValue: string | null): () => boolean {
  // If no search value is provided, return a function that returns false
  if (!searchValue) {
    return () => false;
  }
  return R.pipe(companyOrFullName, R.eqBy(R.toLower, searchValue));
}

const EMPTY_ARRAY = [];

const UICustomerResults: PosPlugin['UICustomerResults'] = ({
  customerPromise,
  searchValue,
  memoizedMapContactsToCompanies,
  children,
}) => {
  const cachedSearchValue = useRef(searchValue);
  cachedSearchValue.current = searchValue;

  const { value: customers = EMPTY_ARRAY } = useAsync(
    () =>
      customerPromise
        .then(customers => {
          const exactMatches = customers.filter(
            isMatchingSearchValue(cachedSearchValue.current),
          );
          if (!exactMatches.length) return customers;
          return R.pipe(
            R.reject(isMatchingSearchValue(cachedSearchValue.current)),
            R.concat(exactMatches),
          )(customers);
        })
        .then(memoizedMapContactsToCompanies),
    [customerPromise, memoizedMapContactsToCompanies],
  );

  return R.pipe(
    React.Children.toArray,
    R.map(
      R.over(
        R.lensPath(['props', 'children']),
        R.pipe(
          React.Children.toArray,
          R.map(child =>
            React.cloneElement(child, {
              customers,
            }),
          ),
        ),
      ),
    ),
  )(children);
};

export default UICustomerResults;
